import { ReparoApi } from './index';

/**
 * The inbound watch: four system alerts, one rule each, the same rules the Needs chips on
 * the Shipments page show. Turned on per brand with one click; idempotent - a type that
 * already exists is left alone. Evaluated every morning; each fires one Home card listing
 * the shipments or payments concerned.
 */
export const INBOUND_ALERT_TYPES = [
    'SHIPMENT_FORWARDER_MISSING',
    'SHIPMENT_DISPATCH_OVERDUE',
    'SHIPMENT_ARRIVAL_OVERDUE',
    'PAYMENT_DUE_SOON',
] as const;

export type InboundAlertType = (typeof INBOUND_ALERT_TYPES)[number];

const LABELS: Record<InboundAlertType, string> = {
    SHIPMENT_FORWARDER_MISSING: 'Shipments ready soon with no forwarder',
    SHIPMENT_DISPATCH_OVERDUE: 'Shipments past their dispatch date',
    SHIPMENT_ARRIVAL_OVERDUE: 'Shipments past their arrival date',
    PAYMENT_DUE_SOON: 'Payments due within 2 days',
};

type UserAlertRow = { alertId: string; alertType: string };

export const listInboundAlerts = async (): Promise<Set<InboundAlertType>> => {
    const rows = await ReparoApi<UserAlertRow[]>({ method: 'GET', url: '/user-alerts', body: null });
    const have = new Set<InboundAlertType>();
    for (const row of rows ?? []) {
        if ((INBOUND_ALERT_TYPES as readonly string[]).includes(row.alertType)) {
            have.add(row.alertType as InboundAlertType);
        }
    }
    return have;
};

/** Creates whichever of the four are missing. Returns how many were created. */
export const ensureInboundAlerts = async (): Promise<number> => {
    const have = await listInboundAlerts();
    let created = 0;
    for (const alertType of INBOUND_ALERT_TYPES) {
        if (have.has(alertType)) continue;
        await ReparoApi<unknown>({
            method: 'POST',
            url: '/user-alerts',
            body: JSON.stringify({
                alertType,
                alertLabel: LABELS[alertType],
                frequency: 'DAILY',
                triggerTime: '08:00',
                isScheduled: true,
            }),
        });
        created += 1;
    }
    return created;
};
